import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EnquiryModal = ({ product, isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    quantity: '1',
    message: '',
    interestedIn: product?.name || ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/?.test(formData?.phone?.replace(/\s+/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const enquiryData = {
        ...formData,
        product: product?.name,
        productId: product?.id,
        timestamp: new Date()?.toISOString(),
        source: 'Product Collection Page'
      };
      
      onSubmit(enquiryData);
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        quantity: '1',
        message: '',
        interestedIn: product?.name || ''
      });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative bg-card rounded-2xl liquid-shadow max-w-2xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="MessageCircle" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-poppins font-bold text-text-primary">
                    Product Enquiry
                  </h3>
                  <p className="text-sm font-inter text-text-secondary">
                    Interested in {product?.name}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                disabled={isSubmitting}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Product Info */}
                <div className="flex items-center space-x-4 p-4 bg-muted rounded-lg">
                  <div className="w-16 h-16 rounded-lg overflow-hidden">
                    <img
                      src={product?.image}
                      alt={product?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-poppins font-semibold text-text-primary">
                      {product?.name}
                    </h4>
                    <p className="text-sm text-text-secondary font-inter">
                      {product?.capacity} • {product?.material}
                    </p>
                    <p className="text-lg font-poppins font-bold text-primary">
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0
                      })?.format(product?.price)}
                    </p>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    error={errors?.name}
                    required
                  />
                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    error={errors?.email}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData?.phone}
                    onChange={handleInputChange}
                    error={errors?.phone}
                    required
                  />
                  <Input
                    label="Company (Optional)"
                    name="company"
                    type="text"
                    placeholder="Enter company name"
                    value={formData?.company}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Quantity */}
                <Input
                  label="Quantity"
                  name="quantity"
                  type="number"
                  placeholder="Enter quantity"
                  value={formData?.quantity}
                  onChange={handleInputChange}
                  min="1"
                  max="1000"
                />

                {/* Message */}
                <div>
                  <label className="block text-sm font-inter font-medium text-text-primary mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="Tell us about your requirements, preferred delivery timeline, or any specific questions..."
                    value={formData?.message}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg font-inter resize-none brand-transition ${
                      errors?.message 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' :'border-border focus:border-primary focus:ring-primary/20'
                    } focus:outline-none focus:ring-4`}
                  />
                  {errors?.message && (
                    <p className="mt-1 text-sm text-red-500 font-inter">{errors?.message}</p>
                  )}
                </div>

                {/* Privacy Notice */}
                <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="Shield" size={20} className="text-accent mt-0.5" />
                    <div>
                      <h5 className="font-inter font-medium text-text-primary mb-1">
                        Privacy & Data Protection
                      </h5>
                      <p className="text-sm text-text-secondary font-inter">
                        Your information is secure and will only be used to process your enquiry. 
                        We follow strict data protection guidelines and never share your details with third parties.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    loading={isSubmitting}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    {isSubmitting ? 'Submitting...' : 'Send Enquiry'}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryModal;